import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Link2, ExternalLink } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  fetchAffiliateLinks, createAffiliateLink, updateAffiliateLink,
  deleteAffiliateLink, fetchAffiliateCategories,
} from "@/lib/api";
import { useBranding } from "@/lib/branding";

const CATEGORY_SUGGESTIONS = [
  "phones", "electronics", "it", "fashion", "home", "kitchen", "beauty",
  "sports", "travel", "finance", "health", "food", "automotive", "gaming",
  "toys", "office", "pets", "garden", "books", "music",
];

export default function AffiliateLinks() {
  const brand = useBranding();
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({
    url: "", shop: "", category: "", label: "", active: true,
    slug: "", template: "redirect", heading: "", subheading: "", cta_label: "",
    accent_color: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, c] = await Promise.all([fetchAffiliateLinks(), fetchAffiliateCategories()]);
      setLinks(l);
      setCategories(c);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url || !form.shop || !form.category) {
      toast.error("URL, shop and category are required");
      return;
    }
    setBusy(true);
    try {
      await createAffiliateLink(form);
      toast.success("Affiliate link saved");
      setForm({
        url: "", shop: "", category: "", label: "", active: true,
        slug: "", template: "redirect", heading: "", subheading: "", cta_label: "",
        accent_color: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id, active) => {
    try {
      await updateAffiliateLink(id, { active });
      load();
    } catch { /* silent */ }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this affiliate link?")) return;
    try {
      await deleteAffiliateLink(id);
      toast.warning("Deleted");
      load();
    } catch { /* silent */ }
  };

  const filtered = filter
    ? links.filter((l) => l.category === filter)
    : links;

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="affiliate-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center gap-3">
          <span className="overline">{brand.brand_name}</span>
          <span className="text-[#D4D4D8]">/</span>
          <span className="text-sm font-medium">Affiliate Links</span>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] flex-1 min-h-0">
          {/* Add form */}
          <form
            onSubmit={submit}
            className="border-b lg:border-b-0 lg:border-r border-[#eaecf0] p-6 space-y-4 bg-white"
            data-testid="affiliate-form"
          >
            <div>
              <div className="overline">New Link</div>
              <div className="font-display text-lg font-medium mt-0.5">Add Affiliate Link</div>
              <p className="text-xs text-[#525252] mt-1">
                Store affiliate URLs by category. Later, matching category domains inject
                these on the lander for extra monetization.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Affiliate URL</Label>
              <Input
                required
                type="url"
                data-testid="affiliate-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…"
                className="rounded-sm font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="overline">Shop</Label>
                <Input
                  required
                  data-testid="affiliate-shop"
                  value={form.shop}
                  onChange={(e) => setForm({ ...form, shop: e.target.value })}
                  placeholder="Amazon"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Category</Label>
                <Input
                  required
                  list="cat-list"
                  data-testid="affiliate-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value.toLowerCase() })}
                  placeholder="phones"
                  className="rounded-sm font-mono"
                />
                <datalist id="cat-list">
                  {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Label (optional)</Label>
              <Input
                data-testid="affiliate-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Buy on Amazon"
                className="rounded-sm"
              />
            </div>

            {/* Public lander customization */}
            <div className="border-t border-dashed border-[#eaecf0] pt-4 space-y-3">
              <div className="overline">Public lander · /a/&lt;slug&gt;</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="overline">Slug (optional)</Label>
                  <Input
                    data-testid="affiliate-slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="auto-from-shop"
                    className="rounded-sm font-mono text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Template</Label>
                  <select
                    data-testid="affiliate-template"
                    value={form.template}
                    onChange={(e) => setForm({ ...form, template: e.target.value })}
                    className="w-full h-9 rounded-sm border border-[#eaecf0] bg-white px-2 text-sm font-mono"
                  >
                    <option value="redirect">redirect · minimal</option>
                    <option value="editorial">editorial · dark hero</option>
                    <option value="poster">poster · cream bold</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Heading (optional)</Label>
                <Input
                  data-testid="affiliate-heading"
                  value={form.heading}
                  onChange={(e) => setForm({ ...form, heading: e.target.value })}
                  placeholder="Continue to Amazon"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Subheading (optional)</Label>
                <Input
                  data-testid="affiliate-subheading"
                  value={form.subheading}
                  onChange={(e) => setForm({ ...form, subheading: e.target.value })}
                  placeholder="Hand-picked partner offers"
                  className="rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="overline">CTA label</Label>
                  <Input
                    data-testid="affiliate-cta"
                    value={form.cta_label}
                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                    placeholder="Continue to shop →"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline">Accent</Label>
                  <Input
                    data-testid="affiliate-accent"
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    placeholder="#03142f"
                    className="rounded-sm font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm">Active</Label>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="affiliate-active"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="affiliate-submit"
              className="w-full rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
              {busy ? "Saving…" : "Add link"}
            </Button>
          </form>

          {/* Table */}
          <div className="flex flex-col min-h-0">
            <div className="border-b border-[#eaecf0] px-6 py-3 flex items-center gap-3">
              <span className="overline">Filter:</span>
              <button
                onClick={() => setFilter("")}
                className={`text-xs font-mono px-2 py-0.5 rounded-sm ${!filter ? "bg-[#03142f] text-white" : "bg-[#F4F4F5] text-[#525252]"}`}
                data-testid="filter-all"
              >
                all ({links.length})
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c}`}
                  className={`text-xs font-mono px-2 py-0.5 rounded-sm ${filter === c ? "bg-[#03142f] text-white" : "bg-[#F4F4F5] text-[#525252]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                    <th className="text-left px-6 py-2.5 overline">Shop</th>
                    <th className="text-left px-4 py-2.5 overline">Category</th>
                    <th className="text-left px-4 py-2.5 overline">Public /a/&lt;slug&gt;</th>
                    <th className="text-left px-4 py-2.5 overline">Template</th>
                    <th className="text-right px-4 py-2.5 overline">Views</th>
                    <th className="text-right px-4 py-2.5 overline">Clicks</th>
                    <th className="text-left px-4 py-2.5 overline">Active</th>
                    <th className="text-right px-6 py-2.5 overline">—</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-16 text-center text-sm text-[#525252]">
                        No affiliate links yet. Add one from the left panel.
                      </td>
                    </tr>
                  )}
                  {filtered.map((l) => {
                    const publicPath = l.slug ? `/a/${l.slug}` : null;
                    const publicUrl = publicPath ? `${window.location.origin}${publicPath}` : null;
                    return (
                    <tr
                      key={l.id}
                      className="border-b border-[#eaecf0] hover:bg-[#FAFAFA]"
                      data-testid={`aff-row-${l.id}`}
                    >
                      <td className="px-6 py-3 font-medium">
                        <div>{l.shop}</div>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noreferrer nofollow"
                          className="font-mono text-[10px] text-[#999] hover:text-[#03142f] flex items-center gap-1 truncate max-w-[220px]"
                          title={l.url}
                        >
                          <Link2 className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                          <span className="truncate">{l.url}</span>
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-[#EFF6FF] text-[#2563EB]">
                          {l.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[260px]">
                        {publicPath ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={publicPath}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-xs text-[#03142f] hover:underline truncate"
                              title={publicUrl}
                              data-testid={`aff-public-${l.slug}`}
                            >
                              /a/{l.slug}
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard?.writeText(publicUrl);
                                toast.success("Copied to clipboard");
                              }}
                              data-testid={`aff-copy-${l.slug}`}
                              className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-[#F4F4F5] text-[#525252] hover:bg-[#eaecf0]"
                            >
                              copy
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#A1A1AA]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-mono text-[#525252]">
                          {l.template || "redirect"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-xs text-[#525252]">
                        {l.views || 0}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-xs text-[#101828]">
                        {l.clicks || 0}
                      </td>
                      <td className="px-4 py-3">
                        <Switch
                          checked={l.active}
                          onCheckedChange={(v) => toggle(l.id, v)}
                          data-testid={`aff-active-${l.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noreferrer nofollow"
                            className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                            title="Open raw affiliate URL"
                          >
                            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </a>
                          <button
                            onClick={() => remove(l.id)}
                            data-testid={`aff-delete-${l.id}`}
                            className="p-1 hover:bg-[#F4F4F5] rounded-sm text-[#BE123C]"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
